import { FC, Fragment, useContext, useState } from "react";
import { useSelector } from "react-redux";
import Link from "next/link";
import { useRouter } from "next/router";
import { useFormik, FormikProvider, FieldArray, FormikProps } from "formik";
import { RedirectableProvider } from "next-auth/client";
import { signIn } from "src/utils/nextAuth";
import prepareNextAuthErrorMessage from "src/utils/prepareNextAuthErrorMessage";
import { AuthProvider } from "pages/api/auth/getUserTokenFromApi";
import { t, Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { AppCtx } from "src/contexts/app.context";
import { Gender, getLocaleGenderIds } from "src/modules/page";
import { googleTagManager } from "src/tracking";
import { isValidEmail } from "src/utils/validations";
import { useImageError } from "src/hooks/useImageError";
import Input from "src/atoms/Input";
import { Portal } from "src/atoms/Portal";
import SalesLandingTextBlock from "./SalesLandingTextBlock";
import { SocialButton } from "src/atoms/SocialButton";
import { PageHead } from "src/molecules/PageHead";
import LogoHeader from "src/organisms/Header/LogoHeader";
import { SingleSalesLanding } from "src/types/graphql.d";
import {
  StyledTopSection,
  StyledTopSectionContent,
  StyledBrandLogo,
  StyledTitle,
  StyledSocialButtonsContainer,
  StyledGenderIdButton,
  StyledButtonWrapper,
  StyledLoginLink,
  StyledBottomSection,
  StyledSalesLandingTextBlockWrapper,
  StyledList,
  StyledListItem,
  StyledListDivider,
  StyledRegisterButton,
} from "./SalesLandingView.styled";
import { Modal } from "src/atoms/Modal";
import { ScreenLoader } from "src/molecules/ScreenLoader";
import { formatName } from "src/utils/formatName";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { sanitize } from "isomorphic-dompurify";
import { useLingui } from "@lingui/react";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";

interface FormikValues {
  email: string;
  shopPreferences: Gender[];
}

interface FormikErrors {
  email?: string;
  shopPreferences?: string;
}

interface Props {
  salesLanding: SingleSalesLanding;
  genderId?: Gender;
}

const SalesLandingView: FC<Props> = ({
  salesLanding,
  genderId: defaultGenderId,
}) => {
  const router = useRouter();
  const { locale } = useContext(AppCtx);
  const [error, setError] = useState<string | undefined>(undefined);
  const { women, men, kids } = useSelector(getLocaleGenderIds);
  const { query } = router;
  const openScreenLoader = query.signin;

  const [setImg, hasImgError] = useImageError();
  const { isMobile } = useBreakpoints();
  const { _, i18n } = useLingui();

  const formik: FormikProps<FormikValues> = useFormik<FormikValues>({
    initialValues: {
      email: "",
      shopPreferences: (defaultGenderId ? [defaultGenderId] : []) as Gender[],
    },
    validate: (values) => {
      const errors: FormikErrors = {};
      if (!isValidEmail(values.email)) {
        errors.email = _(FORM_ERROR_MESSAGES.email);
      }
      if (!values.shopPreferences.length) {
        errors.shopPreferences = _(FORM_ERROR_MESSAGES.selectGender);
      }
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setError(undefined);

      const response = await signIn(
        AuthProvider.CREDENTIALS_SOFT_LOGIN as RedirectableProvider,
        {
          assumedAuthType: "sign_up",
          ...values,
          redirect: false,
        }
      );

      if (response?.url) {
        // NOTE: Assumes that 'response.url' is the same as current URL with '?signin=true'. Push
        // it with { shallow: true } to not run 'getInitialProps' of '/outlet/...' page. Check comments
        // in '/outlet/...' for more details
        void router.push(response.url, undefined, { shallow: true });
      } else if (response?.error) {
        setError(prepareNextAuthErrorMessage(response.error));
      }
    },
  });

  const { saleslanding_data, seo_texts } = salesLanding;

  return (
    <Box>
      {openScreenLoader === "true" && (
        <Modal onClose={() => {}} overFlow={"visible"} isOpen={true}>
          <ScreenLoader />
        </Modal>
      )}
      <PageHead
        title={formatName(seo_texts.seo_title)}
        description={formatName(seo_texts.seo_description)}
        noindex={Boolean(saleslanding_data.is_noindex)}
      />

      <Box sx={{ position: "relative" }}>
        <LogoHeader />

        <StyledTopSection
          url={
            isMobile
              ? saleslanding_data.top_image_mobile
              : saleslanding_data.top_image
          }
        >
          <StyledTopSectionContent>
            {saleslanding_data.logo && !hasImgError ? (
              <StyledBrandLogo ref={setImg} src={saleslanding_data.logo} />
            ) : (
              <StyledTitle>{formatName(saleslanding_data.title)}</StyledTitle>
            )}
            <Box maxWidth="352px" mx="auto">
              <Text color="White" fontSize="18px" mt="16px" mb="24px">
                {formatName(saleslanding_data.intro_text)}
              </Text>

              <StyledSocialButtonsContainer>
                <SocialButton
                  fullWidth
                  type="facebook"
                  onClick={() =>
                    void signIn(AuthProvider.FACEBOOK, {
                      assumedAuthType: "sign_up",
                    })
                  }
                >
                  Facebook
                </SocialButton>
                <SocialButton
                  type="google"
                  fullWidth
                  onClick={() =>
                    void signIn(AuthProvider.GOOGLE, {
                      assumedAuthType: "sign_up",
                    })
                  }
                >
                  Google
                </SocialButton>
              </StyledSocialButtonsContainer>

              <Text
                color="White"
                fontSize="16px"
                mt="24px"
                mb="16px"
                sx={{ "&:first-letter": { textTransform: "uppercase" } }}
              >
                <Trans>or using your email</Trans>
              </Text>

              {!defaultGenderId && (
                <Flex>
                  <FormikProvider value={formik}>
                    <FieldArray
                      name="shopPreferences"
                      render={(arrayHelpers) => (
                        <>
                          {[women, men, kids]
                            .filter(
                              (gender) =>
                                !(locale === "en-us" && gender === "kids")
                            )
                            .map((gender, index) => (
                              <StyledGenderIdButton
                                key={gender}
                                type="button"
                                marginLeft={index > 0}
                                active={formik.values.shopPreferences.includes(
                                  gender as Gender
                                )}
                                onClick={() =>
                                  formik.values.shopPreferences.includes(
                                    gender as Gender
                                  )
                                    ? arrayHelpers.remove(
                                        formik.values.shopPreferences.findIndex(
                                          (shopPreference) =>
                                            shopPreference === gender
                                        )
                                      )
                                    : arrayHelpers.push(gender)
                                }
                              >
                                {_(shopTypeTranslation[gender])}
                              </StyledGenderIdButton>
                            ))}
                        </>
                      )}
                    />
                  </FormikProvider>
                </Flex>
              )}

              <Box>
                <form noValidate onSubmit={formik.handleSubmit}>
                  <Input
                    my={2}
                    backgroundColor="White"
                    type="text"
                    placeholder={t(i18n)`Email address`}
                    {...formik.getFieldProps("email")}
                  />

                  {error && (
                    <Text mb={2} color="semantic.red">
                      {error}
                    </Text>
                  )}

                  <StyledButtonWrapper>
                    <StyledRegisterButton
                      fullWidth
                      type="submit"
                      variant="tertiary"
                      colorScheme="light"
                      disabled={
                        !(formik.isValid && formik.dirty) || formik.isSubmitting
                      }
                    >
                      <Trans>Create an account</Trans>
                    </StyledRegisterButton>
                  </StyledButtonWrapper>
                </form>
              </Box>

              <Text
                color="White"
                fontSize="16px"
                mt={2}
                mb={2}
                sx={{ "&:first-letter": { textTransform: "uppercase" } }}
              >
                <Trans>already registered?</Trans>{" "}
                <Link href="/sign-in/" passHref legacyBehavior>
                  <StyledLoginLink
                    onClick={() => googleTagManager.trackAlreadyRegistered()}
                  >
                    <Trans>Login</Trans>
                  </StyledLoginLink>
                </Link>
              </Text>
            </Box>
          </StyledTopSectionContent>
        </StyledTopSection>

        <StyledBottomSection
          url={
            isMobile
              ? saleslanding_data.bottom_image_mobile
              : saleslanding_data.bottom_image
          }
        >
          {saleslanding_data.usp_text && (
            <StyledSalesLandingTextBlockWrapper>
              <SalesLandingTextBlock>
                <StyledList>
                  {saleslanding_data.usp_text.map((text, index) => (
                    <Fragment key={index}>
                      {index > 0 && <StyledListDivider />}
                      <StyledListItem>{text}</StyledListItem>
                    </Fragment>
                  ))}
                </StyledList>
              </SalesLandingTextBlock>
            </StyledSalesLandingTextBlockWrapper>
          )}
        </StyledBottomSection>
      </Box>

      <Portal selector="#footer-seo-text-portal">
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize(seo_texts.seo_bottom_text),
          }}
        />
      </Portal>
    </Box>
  );
};

export default SalesLandingView;
